import { useMemo, useState, useEffect } from 'react';
import { X, Loader2, RefreshCw, ChevronDown, ChevronRight, PanelLeftClose, PanelLeftOpen } from 'lucide-react';
import { Button } from './ui/button';
import { ScrollArea } from './ui/scroll-area';
import { Input } from './ui/input';

interface MpepViewerProps {
  section: string;
  title?: string;
  content?: { content?: string } | string | null;
  loading: boolean;
  error?: string;
  onClose: () => void;
  onReload: () => void;
  onSectionSelect: (section: string) => void;
  chapters?: any;
  chaptersLoading?: boolean;
}

const processMpepHtml = (html?: string) => {
  if (!html) return '';
  const baseUrl = 'https://www.uspto.gov/web/offices/pac/mpep/';

  // Fix relative image URLs
  let processed = html.replace(
    /<img([^>]*?)src=["'](?!https?:\/\/)([^"']+)["']/gi,
    (match, beforeSrc, relativePath) => {
      const cleanPath = relativePath.replace(/^\.?\//, '');
      const absoluteUrl = baseUrl + cleanPath;
      return `<img${beforeSrc}src="${absoluteUrl}"`;
    }
  );

  // Instead of removing anchor tags completely, just remove the href attribute
  // This preserves all the text content while preventing navigation
  processed = processed.replace(
    /<a\s+([^>]*?)href=["'][^"']*["']([^>]*?)>/gi,
    '<span $1$2>'
  );
  processed = processed.replace(/<\/a>/gi, '</span>');

  return processed;
};

interface NormalizedChapter {
  id: string;
  number: string;
  title: string;
  sections: Array<{ number: string; title?: string }>;
}

const normalizeSectionNumber = (value: string) => {
  if (!value) return '';
  return value.replace(/[^0-9.]/g, '');
};

export function MpepViewer({
  section,
  title,
  content,
  loading,
  error,
  onClose,
  onReload,
  onSectionSelect,
  chapters,
  chaptersLoading,
}: MpepViewerProps) {
  const [sectionInput, setSectionInput] = useState('');
  const [expandedChapters, setExpandedChapters] = useState<Record<string, boolean>>({});
  const [browserCollapsed, setBrowserCollapsed] = useState(true);

  const normalizedChapters: NormalizedChapter[] = useMemo(() => {
    if (!chapters) return [];

    // If already an array in expected shape
    if (Array.isArray(chapters)) {
      return chapters
        .map((chapter: any, index: number) => ({
          id: chapter.id || chapter.number || String(index),
          number: String(chapter.number || chapter.id || index),
          title: chapter.title || `Chapter ${chapter.number || chapter.id || index}`,
          sections: (chapter.sections || []).map((sec: any) => ({
            number: normalizeSectionNumber(typeof sec === 'string' ? sec : sec.number || ''),
            title: typeof sec === 'object' ? sec.title : undefined,
          })).filter(sec => !!sec.number),
        }))
        .sort((a, b) => parseInt(a.number) - parseInt(b.number));
    }

    if (typeof chapters === 'object') {
      return Object.keys(chapters)
        .filter(key => /^\d{4}$/.test(key))
        .sort()
        .map((chapterKey) => {
          const chapterData = chapters[chapterKey] || {};
          const chapterNum = parseInt(chapterKey, 10).toString();
          const rawSections = chapterData.sections || chapterData.subsections || {};
          const sectionEntries = Array.isArray(rawSections)
            ? rawSections
            : Object.keys(rawSections || {}).map(secKey => ({
                number: secKey,
                title: rawSections[secKey]?.title,
              }));

          return {
            id: chapterNum,
            number: chapterNum,
            title: chapterData.title || `Chapter ${chapterNum}`,
            sections: sectionEntries
              .map((sec: any) => ({
                number: normalizeSectionNumber(typeof sec === 'string' ? sec : sec.number || sec.section || ''),
                title: typeof sec === 'object' ? (sec.title || sec.name) : undefined,
              }))
              .filter(sec => !!sec.number),
          };
        });
    }

    return [];
  }, [chapters]);

  useEffect(() => {
    if (!section) return;
    const normalizedCurrent = normalizeSectionNumber(section);
    const matchingChapter = normalizedChapters.find(chapter =>
      chapter.sections.some(sec => normalizeSectionNumber(sec.number) === normalizedCurrent)
    );
    if (matchingChapter) {
      setExpandedChapters(prev =>
        prev[matchingChapter.id]
          ? prev
          : { ...prev, [matchingChapter.id]: true }
      );
    }
  }, [section, normalizedChapters]);

  const htmlContent = useMemo(() => {
    if (!content) return '';
    if (typeof content === 'string') {
      return processMpepHtml(content);
    }
    if (typeof content === 'object' && content.content) {
      return processMpepHtml(content.content);
    }
    return '';
  }, [content]);

  const handleSectionSubmit = () => {
    const normalized = normalizeSectionNumber(sectionInput);
    if (!normalized) return;
    onSectionSelect(normalized);
    setSectionInput('');
  };

  const handleSectionInputKey = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleSectionSubmit();
    }
  };

  const toggleChapter = (chapterId: string) => {
    setExpandedChapters(prev => ({ ...prev, [chapterId]: !prev[chapterId] }));
  };

  const handleSectionClick = (sectionNumber: string) => {
    onSectionSelect(sectionNumber);
  };

  return (
    <div className="flex flex-col h-full min-h-0 bg-white border-l border-gray-200">
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
        <div>
          <p className="text-xs text-gray-500 uppercase tracking-wide">MPEP Section</p>
          <h2 className="text-lg font-semibold text-gray-900">
            {title || `MPEP §${section}`}
          </h2>
          <p className="text-sm text-gray-600">Manual of Patent Examining Procedure</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            className="gap-2"
            onClick={() => setBrowserCollapsed(prev => !prev)}
          >
            {browserCollapsed ? (
              <>
                <PanelLeftOpen className="w-4 h-4" />
                Show Navigator
              </>
            ) : (
              <>
                <PanelLeftClose className="w-4 h-4" />
                Hide Navigator
              </>
            )}
          </Button>
          <Button variant="outline" size="sm" className="gap-2" onClick={onReload} disabled={loading}>
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Reload
          </Button>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="w-5 h-5" />
          </Button>
        </div>
      </div>

      <div className="flex flex-1 min-h-0 overflow-hidden">
        {!browserCollapsed && (
          <div className="w-72 border-r border-gray-200 flex flex-col min-h-0">
            <div className="p-4 space-y-3">
              <div>
                <label className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1 block">
                  Jump to Section
                </label>
                <div className="flex gap-2">
                  <Input
                    placeholder="e.g., 2141"
                    value={sectionInput}
                    onChange={(e) => setSectionInput(e.target.value)}
                    onKeyDown={handleSectionInputKey}
                  />
                  <Button onClick={handleSectionSubmit}>Open</Button>
                </div>
                <p className="text-[11px] text-gray-500 mt-1">Enter digits only (e.g., 2106.04)</p>
              </div>
              <div>
                <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">
                  Chapters
                </p>
                {chaptersLoading ? (
                  <div className="flex items-center gap-2 text-xs text-gray-500">
                    <Loader2 className="w-4 h-4 animate-spin text-blue-600" />
                    Loading chapters...
                  </div>
                ) : normalizedChapters.length === 0 ? (
                  <p className="text-xs text-gray-500">Chapter list unavailable.</p>
                ) : null}
              </div>
            </div>
            <ScrollArea className="flex-1 min-h-0 px-2 pb-4">
              <div className="space-y-1 pr-2">
                {normalizedChapters.map((chapter) => {
                  const isExpanded = expandedChapters[chapter.id];
                  return (
                    <div key={chapter.id} className="border border-gray-100 rounded-lg">
                      <button
                        className="w-full flex items-center justify-between px-3 py-2 text-left hover:bg-gray-50"
                        onClick={() => toggleChapter(chapter.id)}
                      >
                        <div>
                          <p className="text-[11px] uppercase text-gray-500">Chapter {chapter.number}</p>
                          <p className="text-sm text-gray-900 truncate">{chapter.title}</p>
                        </div>
                        {isExpanded ? (
                          <ChevronDown className="w-4 h-4 text-gray-400" />
                        ) : (
                          <ChevronRight className="w-4 h-4 text-gray-400" />
                        )}
                      </button>
                      {isExpanded && chapter.sections.length > 0 && (
                        <div className="border-t border-gray-100 max-h-56 overflow-y-auto">
                          <div className="py-2">
                            {chapter.sections.map((sec) => {
                              const normalized = normalizeSectionNumber(sec.number);
                              const isActive = normalizeSectionNumber(section) === normalized;
                              return (
                                <button
                                  key={normalized}
                                  className={`w-full text-left px-4 py-1.5 text-xs ${
                                    isActive
                                      ? 'bg-blue-50 text-blue-700'
                                      : 'text-gray-700 hover:bg-gray-50'
                                  }`}
                                  onClick={() => handleSectionClick(normalized)}
                                >
                                  MPEP §{normalized}
                                  {sec.title && (
                                    <span className="block text-[11px] text-gray-500 truncate">
                                      {sec.title}
                                    </span>
                                  )}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </ScrollArea>
          </div>
        )}

        <div className="flex-1 flex flex-col min-h-0">
          {loading ? (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center text-gray-500 flex flex-col items-center gap-3">
                <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
                <p className="text-sm">Loading MPEP §{section}...</p>
              </div>
            </div>
          ) : error ? (
            <div className="flex-1 flex items-center justify-center px-6 text-center">
              <div>
                <p className="text-sm text-red-600 font-medium mb-2">Failed to load section</p>
                <p className="text-sm text-gray-600 mb-4">{error}</p>
                <Button onClick={onReload}>Try Again</Button>
              </div>
            </div>
          ) : htmlContent ? (
            <ScrollArea className="flex-1 min-h-0">
              <div
                className="prose prose-sm max-w-none px-6 py-6 text-gray-800"
                dangerouslySetInnerHTML={{ __html: htmlContent }}
              />
            </ScrollArea>
          ) : (
            <div className="flex-1 flex items-center justify-center px-6 text-center">
              <p className="text-sm text-gray-600">
                No content available for this section. Try reloading or selecting a different section.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
